import React from 'react';
import { useInView } from './useInView';

type StaggerProps = {
  as?: keyof JSX.IntrinsicElements;
  step?: number;
  className?: string;
  children: React.ReactNode;
};

/**
 * Reveal children one after another. Each top-level child gets `--i` set to its
 * index; index.css turns that into a `transition-delay`. Children should set
 * `data-reveal` themselves OR be wrapped in <Reveal> if they need their own
 * starting state — Stagger only sequences. Combine with Reveal for the common
 * case (see usage in Home/About hero stacks).
 */
export function Stagger({ as = 'div', step = 80, className = '', children }: StaggerProps) {
  const { ref, inView } = useInView<HTMLElement>();
  const Tag = as as any;

  const items = React.Children.toArray(children).map((child, i) => {
    if (!React.isValidElement(child)) return child;
    const existingStyle = (child.props as any).style ?? {};
    return React.cloneElement(child as React.ReactElement<any>, {
      style: { ...existingStyle, ['--i' as any]: i },
      'data-reveal': inView ? 'in' : 'out',
    });
  });

  return (
    <Tag
      ref={ref}
      data-stagger
      style={{ ['--step' as any]: `${step}ms` }}
      className={className}
    >
      {items}
    </Tag>
  );
}
